import { Suspense, useEffect, useState } from 'react';
import { StyleProvider } from '@ant-design/cssinjs';
import { App as AntdApp, Button, ConfigProvider, Spin, Typography, theme } from 'antd';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isRTL, type Locale } from '@ulucu/shared';
import { MODULES, type ModuleMeta } from './modules/_manifest.js';
import MobileFirstLayout from './components/MobileFirstLayout.js';
import ScrollToTop from './components/ScrollToTop.js';
import { useAuthStore } from './stores/auth.js';
import { capabilityForModule, moduleState, type CapabilityState } from './capabilities.js';
import { getConfiguredRegion, useRegion } from './hooks/useRegion.js';
import { NativeMirrorPage, NativeTopBar } from './components/MobileRootScaffold.js';
import HomeTab, {
	HomeAssessmentPage,
	HomeDynamicsRankingPage,
	HomeEventCreatePage,
	HomeEventDetailPage,
	HomeEventListPage,
	HomeEventPage,
	HomeMessagePage,
	HomeMessageSettingsPage,
	HomeOrganizationPage,
	HomeStoreInspectionMessageDetailPage,
} from './m-tabs/Home.js';
import VideoTab from './m-tabs/Video.js';
import ApplicationTab, { ApplicationDisplaySettings, ApplicationNativeGate } from './m-tabs/Application.js';
import SnapshotInspection from './m-tabs/SnapshotInspection.js';
import StoreDetail from './m-tabs/StoreDetail.js';
import MultiView from './m-tabs/MultiView.js';
import MultiSelect from './m-tabs/MultiSelect.js';
import PlayerTab from './m-tabs/Player.js';
import RepairForm from './m-tabs/Repair.js';
import RepairListPage from './m-tabs/RepairList.js';
import ReportsTab from './m-tabs/Reports.js';
import WorkbenchTab from './m-tabs/Workbench.js';
import MessagesTab from './m-tabs/Messages.js';
import ProfileTab from './m-tabs/Profile.js';
import ProfileEdit from './m-tabs/ProfileEdit.js';
import Recent from './m-tabs/Recent.js';
import Album from './m-tabs/Album.js';
import Hotline from './m-tabs/Hotline.js';
import Feedback from './m-tabs/Feedback.js';
import Changelog from './m-tabs/Changelog.js';
import Settings, { NativeAboutSettings, NativeLanguageSettings, NativePasswordSettings } from './m-tabs/Settings.js';
import { apiUrl } from './utils/api-base.js';
import { fetchUserInfo } from './api/user.js';
import zhCN from 'antd/es/locale/zh_CN';
import zhTW from 'antd/es/locale/zh_TW';
import enUS from 'antd/es/locale/en_US';
import esES from 'antd/es/locale/es_ES';
import arEG from 'antd/es/locale/ar_EG';

const antdLocaleMap = { 'zh-CN': zhCN, 'zh-TW': zhTW, 'en-US': enUS, 'es-ES': esES, 'es-MX': esES, 'ar-SA': arEG };
const BRAND_PRIMARY = '#FF7043';

export default function App() {
	const { i18n } = useTranslation();
	const locale = i18n.language as Locale;
	const direction = isRTL(locale) ? 'rtl' : 'ltr';
	const isAuthenticated = useAuthStore((s) => !!s.token);

	useEffect(() => {
		document.documentElement.lang = locale;
		document.documentElement.dir = direction;
	}, [locale, direction]);

	return (
		<StyleProvider hashPriority="high">
			<ConfigProvider
				locale={antdLocaleMap[locale] ?? zhCN}
				direction={direction}
				theme={{
					algorithm: theme.defaultAlgorithm,
					token: { colorPrimary: BRAND_PRIMARY, borderRadius: 8 },
				}}
			>
				<AntdApp>
					<Suspense fallback={<Spin />}>
						<Routes>
							<Route
								path="/login"
								element={isAuthenticated ? <Navigate to="/" replace /> : <LoginRoute />}
							/>
							<Route
								path="/*"
								element={isAuthenticated ? <MainShell /> : <Navigate to="/login" replace />}
							/>
						</Routes>
					</Suspense>
				</AntdApp>
			</ConfigProvider>
		</StyleProvider>
	);
}

function LoginRoute() {
	const Login = MODULES.find((m) => m.key === 'auth')!.component;
	return <Login />;
}

function RegionLanding({ fallbackRegion }: { fallbackRegion: 'cn' | 'apac' }) {
	const [target, setTarget] = useState<string | null>(null);
	useEffect(() => {
		const configuredRegion = getConfiguredRegion();
		if (configuredRegion) {
			setTarget(configuredRegion === 'apac' ? '/m/home' : '/m/monitor');
			return;
		}
		let cancelled = false;
		fetch(apiUrl('/api/internal/region'))
			.then((res) => res.json())
			.then((data: { region?: string }) => {
				const region = data.region === 'apac' ? 'apac' : 'cn';
				try { localStorage.setItem('ulucu.backend.region', region); } catch { /* ignore */ }
				if (!cancelled) setTarget(region === 'apac' ? '/m/home' : '/m/monitor');
			})
			.catch(() => {
				if (!cancelled) setTarget('/m/monitor');
			});
		return () => { cancelled = true; };
	}, [fallbackRegion]);

	if (target) return <Navigate to={target} replace />;
	return (
		<div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
			<Spin />
		</div>
	);
}

function MainShell() {
	const region = useRegion();
	const token = useAuthStore((s) => s.token);

	useEffect(() => {
		if (!token) return;
		void fetchUserInfo();
	}, [token]);

	return (
		<MobileFirstLayout>
			<ScrollToTop />
			<Routes>
				{/* 根路径统一进 APAC / Mexico 一级入口 */}
				<Route path="/" element={<RegionLanding fallbackRegion={region} />} />

				{/* 手机模式 5 tab：新 IA 路由 + 老路由兼容 */}
				<Route path="/m/home" element={<HomeTab />} />
				<Route path="/m/home/organization" element={<HomeOrganizationPage />} />
				<Route path="/m/home/dynamics-ranking" element={<HomeDynamicsRankingPage />} />
				<Route path="/m/home/messages" element={<HomeMessagePage />} />
				<Route path="/m/home/messages/shop-inspection/:messageId" element={<HomeStoreInspectionMessageDetailPage />} />
				<Route path="/m/home/messages/settings" element={<HomeMessageSettingsPage />} />
				<Route path="/m/home/assessment" element={<HomeAssessmentPage />} />
				<Route path="/m/home/assessment/:view" element={<HomeAssessmentPage />} />
				<Route path="/m/home/events" element={<HomeEventPage />} />
				<Route path="/m/home/events/create" element={<HomeEventCreatePage />} />
				<Route path="/m/home/events/list/:status" element={<HomeEventListPage />} />
				<Route path="/m/home/events/detail/:eventId" element={<HomeEventDetailPage />} />
				<Route path="/m/home/events/:kind" element={<HomeEventPage />} />
				<Route path="/m/monitor" element={<VideoTab />} />
				<Route path="/m/video" element={<VideoTab />} />
				<Route path="/m/video/:storeId" element={<StoreDetail />} />
				<Route path="/m/multi-view/:storeId" element={<MultiView />} />
				<Route path="/m/multi-select/:storeId" element={<MultiSelect />} />
				<Route path="/m/player" element={<PlayerTab />} />
				<Route path="/m/repair" element={<RepairForm />} />
				<Route path="/m/repair-list" element={<RepairListPage />} />
				<Route path="/m/analytics" element={<ReportsTab />} />
				<Route path="/m/reports" element={<ReportsTab />} />
				<Route path="/m/application" element={<ApplicationTab />} />
				<Route path="/m/application/display" element={<ApplicationDisplaySettings />} />
				<Route path="/m/application/native-gate/:itemKey" element={<ApplicationNativeGate />} />
				<Route path="/m/snapshot-inspection" element={<SnapshotInspection />} />
				<Route path="/m/operations" element={<WorkbenchTab />} />
				<Route path="/m/workbench" element={<WorkbenchTab />} />
				<Route path="/m/incidents" element={<MessagesTab />} />
				<Route path="/m/messages" element={<MessagesTab />} />
				<Route path="/m/account" element={<ProfileTab />} />
				<Route path="/m/profile" element={<ProfileTab />} />
				<Route path="/m/profile-edit" element={<ProfileEdit />} />
				<Route path="/m/recent" element={<Recent />} />
				<Route path="/m/album" element={<Album />} />
				<Route path="/m/hotline" element={<Hotline />} />
				<Route path="/m/feedback" element={<Feedback />} />
				<Route path="/m/changelog" element={<Changelog />} />
				<Route path="/m/settings" element={<Settings />} />
				<Route path="/m/settings/password" element={<NativePasswordSettings />} />
				<Route path="/m/settings/language" element={<NativeLanguageSettings />} />
				<Route path="/m/settings/about" element={<NativeAboutSettings />} />

				{/* 桌面模式（27 模块）+ 手机模式深链跳进单模块 */}
				{MODULES
					.filter((m) => m.key !== 'auth')
					.map((m) => {
						return <Route key={m.key} path={m.path} element={<ModuleRoute module={m} />} />;
					})}
			</Routes>
		</MobileFirstLayout>
	);
}

function ModuleRoute({ module }: { module: ModuleMeta }) {
	const region = useRegion();
	const state = moduleState(module, region);
	const Component = module.component;
	if (state === 'available') return <Component />;
	return <CapabilityGate module={module} state={state} />;
}

function CapabilityGate({ module, state }: { module: ModuleMeta; state: CapabilityState }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	const cap = capabilityForModule(module.key);
	const title = cap ? t(cap.labelKey) : t(`modules.${module.key}`);
	const bodyKey = state === 'permission_required'
		? 'capability.permission_required_desc'
		: state === 'unknown'
			? 'capability.unknown_desc'
			: 'capability.region_unavailable_desc';

	if (region === 'apac') {
		return (
			<NativeMirrorPage>
				<NativeTopBar title={t('native.application.function_intro')} onBack={() => navigate('/m/application', { replace: true })} />
				<div style={{ minHeight: 260, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
					<div style={{ color: '#4f5661', fontSize: 15, lineHeight: 1.45 }}>
						{t('native.application.permission_denied')}
					</div>
				</div>
			</NativeMirrorPage>
		);
	}

	return (
		<div style={{
			minHeight: '100vh',
			background: '#f5f5f5',
			padding: 24,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		}}>
			<div style={{
				width: 'min(440px, 100%)',
				background: '#fff',
				borderRadius: 12,
				padding: 24,
				boxShadow: '0 8px 28px rgba(0,0,0,0.06)',
			}}>
				<Typography.Title level={4} style={{ marginTop: 0 }}>{title}</Typography.Title>
				<Typography.Paragraph type="secondary">
					{t(bodyKey)}
				</Typography.Paragraph>
				<Button type="primary" onClick={() => navigate('/m/operations', { replace: true })}>
					{t('capability.back_to_operations')}
				</Button>
			</div>
		</div>
	);
}
